<template>
  <div class="open-orders" :class="{ mobile: $mq === 'mobile' }">
    <ic-tab
    v-model="currentKey"
    :items="[
      { text: $t('trade.open-orders'), key: 'open' },
      { text: $t('trade.order-history'), key: 'orders' },
      { text: $t('trade.trade-history'), key: 'trades' },
    ]"
    />
    <duration-filter
      v-if="currentKey !== 'open'"
      @dateFilterChange="dateFilterChange"
    />

    <div class="table-container">
      <div v-if="isDataLoading" class="d-flex justify-content-center align-items-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
      <table v-else-if="tableData.length !== 0" class="ic-table">
        <thead>
          <tr>
            <th
              v-for="(head, i) in tableHead"
              :key="`table-head-${i}`"
              :class="{ currency: head.currency }"
            >
              {{ head.text }}
            </th>
          </tr>
        </thead>

        <tbody v-if="tableData">
          <tr :key="`table-row-${i}`" v-for="(item, i) in tableData">
            <template v-if="currentKey === 'open'">
              <td>{{ i + 1 }}</td>
              <td>{{ item.created_at | formatDate('dateTime') }}</td>
              <td
                :class="['side-cell', item.side === 1 ? 'is-buy' : 'is-sell']"
                v-text="item.side === 1 ? $t('order.buy') : $t('order.sell')"
              ></td>
              <td>{{ item.symbol | symbol }} <span class="exchange-code">/ {{ item.exchange_server_code ?? '--' }}</span></td>
              <td class="currency">
                {{ item.unit_price | currency('', '₮') }}
              </td>
              <td class="currency">
                <template>
                  {{ item.qty | currency('no-precision', '') }}
                </template>
              </td>
              <td class="currency">
                <template>
                  {{ item.exec_qty | currency('no-precision', '') }}
                </template>
              </td>
              <td class="currency">
                <template>
                  {{ item.qty - item.exec_qty | currency('no-precision', '') }}
                </template>
              </td>
              <td class="currency">
                {{ (item.total_amount || item.order_amount) | currency('', '₮') }}
              </td>
              <td>
                <ic-button
                  block
                  size="tn"
                  color="grey"
                  class="py-1"
                  :disabled="isCancelDisabled(item)"
                  @click="cancelOrder(item)"
                >
                  <i class="sr sr-trashcan"></i>
                </ic-button>
              </td>
            </template>

            <template v-else-if="currentKey === 'orders'">
              <td>{{ i + 1 }}</td>
              <td>{{ item.updated_at | formatDate('dateTime') }}</td>
              <td
                :class="['side-cell', item.side === 1 ? 'is-buy' : 'is-sell']"
                v-text="item.side === 1 ? $t('order.buy') : $t('order.sell')"
              ></td>
              <td>{{ item.symbol | symbol }}  <span class="exchange-code">/ {{ item.exchange_server_code ?? '--' }}</span></td>
              <td class="currency">
                {{ item.unit_price | currency('', '₮') }}
              </td>
              <td class="currency">
                <template>
                  {{ item.quantity | currency('no-precision', '') }}
                </template>
              </td>
              <td class="currency">
                <template v-if="item.order_details">
                  {{
                    calcLast(item.order_details) | currency('no-precision', '')
                  }}
                </template>
                <template v-else-if="item.last_qty">
                  {{ item.last_qty }}</template
                >
                <template v-else>-</template>
              </td>
              <td class="currency">
                <template v-if="item.order_details">
                  {{
                    (item.quantity - calcLast(item.order_details))
                      | currency('no-precision', '')
                  }}
                </template>
                <template v-else-if="item.leaves_qty">
                  {{ item.leaves_qty }}</template
                >
                <template v-else>-</template>
              </td>
              <td class="currency">
                <template>
                  {{ item.total_amount | currency('', '₮') }}
                </template>
              </td>
              <td>{{ ExecType[item.exec_type].name }}</td>
            </template>

            <template v-else-if="currentKey === 'trades'">
              <td>{{ i + 1 }}</td>
              <td>{{ item.created_at | formatDate('dateTime') }}</td>
              <td
                :class="['side-cell', item.side === 1 ? 'is-buy' : 'is-sell']"
                v-text="item.side === 1 ? $t('order.buy') : $t('order.sell')"
              ></td>
              <td>{{ item.symbol | symbol }} <span class="exchange-code">/ {{ item.exchange_server_code ?? '--' }}</span></td>
              <td class="currency">
                {{ item.unit_price | currency('', '₮') }}
              </td>
              <td class="currency">
                {{ item.quantity | currency('no-precision', '') }}
              </td>
              <td></td>
              <td class="currency">
                {{ item.total_amount | currency('', '₮') }}
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>

    <empty
      v-if="!isDataLoading && currentKey === 'open' && openOrders.length === 0"
      type="order"
    />
    <empty
      v-else-if="!isDataLoading && currentKey === 'orders' && brandOrderHistory.length === 0"
      type="order"
    />
    <empty
      v-else-if="!isDataLoading && currentKey === 'trades' && brandTradeHistory.length === 0"
      type="trade"
    />
  </div>
</template>

<script>
import DurationFilter from '../DurationFilter'
import Empty from '../Empty'
import { ExecType } from '../../constants'
import { mapState } from 'vuex'
import moment from 'moment'
import api from '../../api/stoTrade'

export default {
  components: {
    DurationFilter,
    Empty,
  },
  data() {
    return {
      currentKey: 'open',
      ExecType,
      durations: null,
      brandOrderHistory: [],
      brandTradeHistory: [],
      isLoading: false,
      isDataLoading: false,
      lastFetchedData: null, // Track what was last fetched to prevent duplicates
    }
  },
  computed: {
    ...mapState('brand', ['brand']),
    ...mapState('cent', ['token', 'instance', 'connected']),
    ...mapState('user', ['user']),
    orderHistory() {
      return this.$store.getters['orderHistory']
    },
    openOrders() {
      return this.$store.getters['openOrders']
    },

    tableData() {
      switch (this.currentKey) {
        case 'open':
          return this.openOrders
        case 'orders':
          return this.brandOrderHistory
        case 'trades':
          return this.brandTradeHistory
        default:
          return this.openOrders
      }
    },
    tableHead() {
      switch (this.currentKey) {
        case 'open':
          return [
            { text: '№', currency: false },
            { text: 'Огноо', currency: false },
            { text: 'Төрөл', currency: false },
            { text: 'Симбол', currency: false },
            { text: 'Үнэ', currency: true },
            { text: 'Нийт ширхэг', currency: true },
            { text: 'Биелсэн', currency: true },
            { text: 'Үлдсэн', currency: true },
            { text: 'Нийт', currency: true },
            { text: 'Үйлдэл', currency: true },
          ]
        case 'orders':
          return [
            { text: '№', currency: false },
            { text: 'Огноо', currency: false },
            { text: 'Төрөл', currency: false },
            { text: 'Симбол', currency: false },
            { text: 'Үнэ', currency: true },
            { text: 'Ширхэг', currency: true },
            { text: 'Биелсэн хэмжээ', currency: true },
            { text: 'Үлдсэн хэмжээ', currency: true },
            { text: 'Нийт', currency: true },
            { text: 'Төлөв', currency: false },
          ]
        case 'trades':
          return [
            { text: '№', currency: false },
            { text: 'Огноо', currency: false },
            { text: 'Төрөл', currency: false },
            { text: 'Симбол', currency: false },
            { text: 'Үнэ', currency: true },
            { text: 'Ширхэг', currency: true },
            { text: 'Нийт', currency: true },
          ]
      }
    },
    filter() {
      const from = moment(this.durations.start).format('YYYY-MM-DD')
      const to = moment(this.durations.end).format('YYYY-MM-DD')
      return { from, to }
    },
  },
  created() {
    this.$store.dispatch('fetchOpenOrders')
    this.durations = {
      start: moment().subtract(1, 'day').toDate(),
      end: new Date(),
    }
  },
  watch: {
    connected: {
      handler: 'cent',
    },
    user: {
      handler: 'cent',
    },
    currentKey: {
      async handler(newKey) {
        if ((newKey === 'orders' || newKey === 'trades') && this.durations) {
          const fetchKey = `${newKey}-${this.filter.from}-${this.filter.to}`
          if (this.lastFetchedData === fetchKey) return // Prevent duplicate calls
          
          this.isDataLoading = true
          this.lastFetchedData = fetchKey
          
          try {
            if (newKey === 'orders') {
              await this.fetchBrandOrderHistory()
            } else {
              await this.fetchBrandTradeHistory()
            }
          } finally {
            this.isDataLoading = false
          }
        }
      },
    },
    orderHistory: {
      handler() {
        this.$forceUpdate()
      },
      deep: true,
    },
  },
  methods: {
    async cent() {
      if (!this.connected) { return }

      if (this.user)
        this.instance.subscribe(`order-state#${this.user.id}`, () => {
          this.$store.dispatch('fetchOpenOrders')
          this.fetchBrandOrderHistory()
          this.fetchBrandTradeHistory()
          this.$store.dispatch('fetchLocalBalance')
          this.$store.dispatch('fetchShareBalance')
        })
    },
    async fetchBrandOrderHistory() {
      try {
        const { data } = await api.orderHistory(this.filter)
        this.brandOrderHistory = data.data
      } catch (e) {
        console.error(e)
      }
    },
    async fetchBrandTradeHistory() {
      try {
        const { data } = await api.tradeHistory(this.filter)
        this.brandTradeHistory = data.data
      } catch (e) {
        console.error(e)
      }
    },
    calcLast(val) {
      return val.reduce((sum, order) => sum + parseInt(order.last_qty), 0)
    },
    dateFilterChange(val) {
      if (val.hasOwnProperty('start')) {
        this.durations = val
        
        // Only fetch data for the currently visible tab
        if (this.currentKey === 'orders' || this.currentKey === 'trades') {
          const fetchKey = `${this.currentKey}-${this.filter.from}-${this.filter.to}`
          if (this.lastFetchedData === fetchKey) return // Prevent duplicate calls
          
          this.isDataLoading = true
          this.lastFetchedData = fetchKey
          
          let fetchPromise
          if (this.currentKey === 'orders') {
            fetchPromise = this.fetchBrandOrderHistory()
          } else {
            fetchPromise = this.fetchBrandTradeHistory()
          }
          
          fetchPromise.finally(() => {
            this.isDataLoading = false
          })
        }
      }
    },
    isCancelDisabled() {
      // return brand.sent === 3
      return false
    },
    cancelOrder(item) {
      this.$swal({
        icon: 'warning',
        title: `${this.toSymbolName(
          item.symbol
        )} | ${this.$options.filters.currency(
          item.unit_price,
          '',
          '₮'
        )} x ${this.$options.filters.amount(item.qty)}`,
        text: this.$t('helper.cancel-title', {
          slot: this.$tc('order._order', 1),
        }),
        confirmButtonText: this.$t('helper.cancel'),
        confirmButtonColor: '#ff7674',
        cancelButtonColor: '#c3c3c3',
        cancelButtonText: this.$t('helper.refuse'),
        showCancelButton: true,
        showConfirmButton: true,
        reverseButtons: false,
      })
        .then(async ({ dismiss, value }) => {
          if (!dismiss && !!value) {
            this.isLoading = true
            try {
              const { data } = await api.cancelOrder(item)
              // await this.$store.dispatch('fetchOrderHistory')
              this.$bvToast.toast(data && data.success === true ? this.$t('helper.cancel-success') : data.message, {
                title: data.message,
                variant: data.success ? 'info' : 'warning',
              })
              this.$store.dispatch('fetchOpenOrders')
            } catch (e) {
              this.$bvToast.toast('Алдаа гарлаа', {
                title: 'Захиалгыг цуцлахад алдаа гарлаа',
                variant: 'info',
              })
              console.error(e)
            }
            this.isLoading = false
          }
        })
        .catch((err) => {
          this.$bvToast.toast('Алдаа гарлаа', {
            title: 'Захиалгыг цуцлахад алдаа гарлаа',
            variant: 'danger',
          })
          console.error(err)
        })
    },
  },
}
</script>

<style lang="scss">
@import './OpenOrders.scss';
</style>
